// src/components/sidebar/sidebar-nav.jsx
import React, { useEffect, useMemo } from 'react';
import { useResolvedPath, useMatch, NavLink, useLocation, matchPath } from 'react-router-dom';
import { useSelector, shallowEqual } from 'react-redux';
import menus from './../../config/app-menu.jsx';
import filterMenuByPermissions from '../../utils/filterMenuByPermissions';
import { slideUp } from './../../composables/slideUp.js';
import { slideToggle } from './../../composables/slideToggle.js';

const EMPTY = Object.freeze([]);

/**
 * Ensure we don't render "empty folders":
 * - Treat empty arrays as NO children (children?.length)
 * - After permission filtering, drop items with children: [] (or only dividers)
 * - Cleanup leading/trailing/repeated dividers
 */
function normalizeMenus(items) {
  if (!Array.isArray(items)) return [];

  // First pass: recursively normalize children and drop empty parents
  const normalized = items
    .map((item) => {
      if (!item) return null;

      // Keep headers/dividers for now; we'll cleanup dividers after
      if (item.is_header || item.is_divider) return item;

      if (Array.isArray(item.children)) {
        const children = normalizeMenus(item.children);

        // If parent has children but all got removed, drop the parent
        if (children.length === 0) return null;

        // If children exists, keep only normalized children
        return { ...item, children };
      }

      return item;
    })
    .filter(Boolean);

  // Second pass: remove redundant dividers (start/end, adjacent to header/divider)
  const cleaned = [];
  for (let i = 0; i < normalized.length; i++) {
    const cur = normalized[i];
    const prev = cleaned[cleaned.length - 1];
    const next = normalized[i + 1];

    if (cur?.is_divider) {
      // drop divider if:
      // - first
      // - last
      // - prev is divider/header
      // - next is divider/header
      if (!prev || !next || prev.is_divider || prev.is_header || next.is_divider || next.is_header) {
        continue;
      }
    }

    cleaned.push(cur);
  }

  return cleaned;
}

function NavItem({ menu, ...props }) {
  const path = menu.path ? menu.path : '';
  const resolved = useResolvedPath(path);
  const match = useMatch({ path: resolved.pathname });
  const location = useLocation();

  useEffect(() => {
    const expandTime = 0;

    const handleClick = function (e) {
      e.preventDefault();
      const target = this.nextElementSibling;

      // Only close siblings at the same level
      const parentMenu = this.closest('.menu-submenu') || this.closest('.menu');
      const siblingMenus = parentMenu ? Array.from(parentMenu.children) : [];

      siblingMenus.forEach((menuEl) => {
        const otherTarget = menuEl.querySelector('.menu-submenu');
        if (otherTarget && otherTarget !== target) {
          slideUp(otherTarget, expandTime);

          const otherTargetMenuItem = otherTarget.closest('.menu-item');
          if (otherTargetMenuItem?.classList) {
            otherTargetMenuItem.classList.remove('expand');
            otherTargetMenuItem.classList.add('closed');
          }
        }
      });

      const targetItemElm = target?.closest('.menu-item');
      if (targetItemElm?.classList) {
        if (
          targetItemElm.classList.contains('expand') ||
          (targetItemElm.classList.contains('active') && !target.style.display)
        ) {
          targetItemElm.classList.remove('expand');
          targetItemElm.classList.add('closed');
          slideToggle(target, expandTime);
        } else {
          targetItemElm.classList.add('expand');
          targetItemElm.classList.remove('closed');
          slideToggle(target, expandTime);
        }
      }
    };

    const menuBaseSelector = '.app-sidebar .menu > .menu-item.has-sub';
    const submenuBaseSelector = ' > .menu-submenu > .menu-item.has-sub';

    const topLevelLinks = Array.from(document.querySelectorAll(menuBaseSelector + ' > .menu-link'));
    const submenusLvl1Links = Array.from(
      document.querySelectorAll(menuBaseSelector + submenuBaseSelector + ' > .menu-link')
    );
    const submenusLvl2Links = Array.from(
      document.querySelectorAll(menuBaseSelector + submenuBaseSelector + submenuBaseSelector + ' > .menu-link')
    );

    const allLinks = [...topLevelLinks, ...submenusLvl1Links, ...submenusLvl2Links];
    allLinks.forEach((link) => {
      link.removeEventListener('click', handleClick);
      link.addEventListener('click', handleClick);
    });

    return () => {
      allLinks.forEach((link) => link.removeEventListener('click', handleClick));
    };
  }, []);

  if (menu.is_header) return <div className="menu-header">{menu.title}</div>;
  if (menu.is_divider) return <div className="menu-divider"></div>;

  const match2 = matchPath({ path, end: false }, location.pathname);

  const icon = menu.icon ? (
    <div className="menu-icon">
      <i className={menu.icon}></i>
    </div>
  ) : null;

  const img = menu.img ? (
    <div className="menu-icon-img">
      <img src={menu.img} alt="" />
    </div>
  ) : null;

  const hasChildren = Array.isArray(menu.children) && menu.children.length > 0;

  const caret = hasChildren && !menu.badge ? (
    <div className="menu-caret">
      <b className="caret"></b>
    </div>
  ) : null;

  const label = menu.label ? <span className="menu-label ms-5px">{menu.label}</span> : null;
  const badge = menu.badge ? <div className="menu-badge">{menu.badge}</div> : null;
  const highlight = menu.highlight ? <i className="fa fa-paper-plane text-theme"></i> : null;

  const title = menu.title ? (
    <div className="menu-text">
      {menu.title} {label} {highlight}
    </div>
  ) : null;

  return (
    <div className={'menu-item' + ((match || match2) ? ' active' : '') + (hasChildren ? ' has-sub' : '')}>
      <NavLink className="menu-link" to={menu.path} {...props}>
        {img} {icon} {title}
        {caret} {badge}
      </NavLink>

      {hasChildren ? (
        <div className="menu-submenu">
          {menu.children.map((submenu, i) => (
            <NavItem key={submenu.path || submenu.title || i} menu={submenu} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function SidebarNav() {
  // Redux permissions (stable)
  const permsFromRedux = useSelector((s) => s.auth.permissions, shallowEqual) || EMPTY;
  const isAdmin = useSelector((s) => Number(s.auth?.logininfo?.adminFlag) === 1);

  // localStorage fallback (in case sidebar renders before Redux is hydrated)
  const permsFallback = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('permissions') || '[]');
    } catch {
      return [];
    }
  }, []);

  const perms = permsFromRedux.length ? permsFromRedux : permsFallback;

  // Filter by permissions, then normalize to remove empty folders + clean dividers
  const visibleMenus = useMemo(() => {
    const filtered = filterMenuByPermissions(menus, perms, isAdmin);
    return normalizeMenus(filtered);
  }, [perms, isAdmin]);

  return (
    <div className="menu">
      {visibleMenus.map((menu, i) => (
        <NavItem key={menu.path || menu.title || i} menu={menu} />
      ))}
    </div>
  );
}

export default SidebarNav;
